export type ProviderModeId = "multiple" | "single";

export type StatusKey = "verified" | "configured" | "notSet" | "error";

export type StatusTone = "success" | "info" | "neutral" | "danger";

export type StageKey = "crd" | "dtr" | "pas";

export type StageTone = StageKey;

export type BadgeTone = StatusTone | StageTone;

export interface ModeOption {
  id: ProviderModeId;
  label: string;
}

export interface TabOption {
  id: string;
  label: string;
  stage?: StageKey;
}

export interface StatusTypeMeta {
  label: string;
  tone: StatusTone;
  icon: string;
}

export interface StageMeta {
  label: string;
  icon: string;
  tone: StageTone;
}

export const AUTH_TYPE = {
  BearerToken: "bearer_token",
  OAuth2ClientCredentials: "oauth2_client_credentials",
  Basic: "basic",
  ApiKeyHeader: "api_key_header",
} as const;

export type AuthTypeId = (typeof AUTH_TYPE)[keyof typeof AUTH_TYPE];

export const DEFAULT_AUTH_TYPE: AuthTypeId = AUTH_TYPE.OAuth2ClientCredentials;

export type HttpMethodId = "GET" | "POST";

export interface KeyValuePair {
  id: string;
  key: string;
  value: string;
}

export interface StageConfig {
  status: StatusKey;
  endpoint?: string;
  source?: string;
  note?: string;
  description?: string;
  authType?: AuthTypeId;
  tokenUrl?: string;
  clientId?: string;
  clientSecret?: string;
  scope?: string;
  bearerToken?: string;
  username?: string;
  password?: string;
  headerName?: string;
  apiKey?: string;
  questionSource?: string;
  requestFormat?: string;
  responseFormat?: string;
  samplePayload?: string;
  httpMethod?: HttpMethodId;
  crdEndpointType?: string;
  requestUrls?: KeyValuePair[];
  /** Connection headers → details */
  headers?: KeyValuePair[];
  /** Credential headers → credentials */
  credentialHeaders?: KeyValuePair[];
  queryParams?: KeyValuePair[];
  requestBody?: KeyValuePair[];
  testResult?: ConnectionTestResult;
  /** From master IsDeleted — false = Active, true = Inactive */
  isDeleted?: boolean;
}

export interface ConnectionTestResult {
  message: string;
  status: "success" | "error";
}

export interface AuthTypeOption {
  value: AuthTypeId;
  label: string;
}

export const AUTH_TYPE_OPTIONS: AuthTypeOption[] = [
  { value: AUTH_TYPE.BearerToken, label: "Bearer Token (Static)" },
  {
    value: AUTH_TYPE.OAuth2ClientCredentials,
    label: "OAuth 2.0 (Client Credentials)",
  },
  { value: AUTH_TYPE.Basic, label: "Basic (Username/Password)" },
  { value: AUTH_TYPE.ApiKeyHeader, label: "API Key + Header" },
];

export interface HttpMethodOption {
  value: HttpMethodId;
  label: string;
}

export const HTTP_METHOD_OPTIONS: HttpMethodOption[] = [
  { value: "GET", label: "GET" },
  { value: "POST", label: "POST" },
];

export const STAGE_DESCRIPTIONS: Record<StageKey, string> = {
  crd: "This vendor's coverage-check endpoint.",
  dtr: "This vendor's questionnaire source.",
  pas: "This vendor's authorization submit endpoint.",
};

export interface PasFormatOption {
  value: string;
  label: string;
}

export const PAS_FORMAT_OPTIONS: PasFormatOption[] = [
  { value: "fhir", label: "FHIR Bundle" },
  { value: "x12_278", label: "EDI 278" },
];

export const PAS_SAMPLE_PAYLOAD = `{
  "resourceType": "Bundle",
  "type": "collection",
  "entry": [
    { "resource": { "resourceType": "Claim", "id": "pa-2291" } }
  ]
}`;

export const PAS_EDI_SAMPLE_PAYLOAD = `ISA*00*          *00*          *ZZ*SENDER         *ZZ*RECEIVER       *260903*1200*^*00501*000000001*0*P*:~
GS*HI*SENDER*RECEIVER*20260903*1200*1*X*005010X217~
ST*278*0001*005010X217~
BHT*0007*13*PA2291*20260903*1200~
HL*1**20*1~
NM1*X3*2*PAYER NAME*****PI*PAYERID~
HL*2*1*21*1~
NM1*1P*2*PROVIDER NAME*****XX*1234567890~
HL*3*2*22*0~
NM1*IL*1*DOE*JOHN****MI*MEM123~
UM*HS*I*73~
SE*10*0001~
GE*1*1~
IEA*1*000000001~`;

/** Normalize legacy format values and return the static preview sample. */
export const getPasSamplePayload = (format?: string) => {
  const normalized = (format || "fhir").toLowerCase();
  if (normalized === "x12_278" || normalized.includes("edi") || normalized.includes("278")) {
    return PAS_EDI_SAMPLE_PAYLOAD;
  }
  return PAS_SAMPLE_PAYLOAD;
};

/** Map stored/legacy format values onto PAS_FORMAT_OPTIONS values. */
export const normalizePasFormat = (format?: string) => {
  const normalized = (format || "fhir").toLowerCase();
  if (normalized === "x12_278" || normalized.includes("edi") || normalized.includes("278")) {
    return "x12_278";
  }
  return "fhir";
};

export interface ServiceProvider {
  id: string;
  shortName: string;
  name: string;
  path: string;
  status: string;
  note?: string;
  crd: StatusKey;
  dtr: StatusKey;
  pas: StatusKey;
  cptCodes: number;
  assignedCodes?: string[];
  stages: Record<StageKey, StageConfig>;
}

export interface SummaryStatBadge {
  text: string;
  tone: StatusTone;
  value?: string;
}

export interface SummaryStat {
  id: string;
  title: string;
  badge: SummaryStatBadge;
  icon: string;
  iconTone: StatusTone;
  detail: string;
}

export type UnmatchedCptPolicyId = "reject" | "route_default";

export interface UnmatchedCptPolicyOption {
  id: UnmatchedCptPolicyId;
  label: string;
}

export const UNMATCHED_CPT_POLICY_OPTIONS: UnmatchedCptPolicyOption[] = [
  { id: "reject", label: "Reject request" },
  { id: "route_default", label: "Route to default" },
];

export const DEFAULT_UNMATCHED_CPT_POLICY: UnmatchedCptPolicyId = "reject";


export const PROVIDER_MODES: ModeOption[] = [
  { id: "multiple", label: "Multiple providers" },
  { id: "single", label: "Single provider" },
];

export const INTEGRATION_TABS: TabOption[] = [
  { id: "overview", label: "Overview" },
  { id: "providers", label: "Service Providers" },
];

export const PROVIDER_DETAIL_TABS: TabOption[] = [
  { id: "summary", label: "Summary" },
  { id: "crd", label: "CRD", stage: "crd" },
  { id: "dtr", label: "DTR", stage: "dtr" },
  { id: "pas", label: "PAS", stage: "pas" },
  { id: "cpt", label: "CPT Codes" },
];

export const STATUS_TYPES: Record<StatusKey, StatusTypeMeta> = {
  verified: {
    label: "Verified",
    tone: "success",
    icon: "fh_check_circle_line",
  },
  configured: {
    label: "Configured",
    tone: "info",
    icon: "fh_settings_line",
  },
  notSet: {
    label: "Not set",
    tone: "neutral",
    icon: "fh_pending_process_line",
  },
  error: {
    label: "Error",
    tone: "danger",
    icon: "fh_error_line",
  },
};

export const STAGE_META: Record<StageKey, StageMeta> = {
  crd: {
    label: "CRD",
    icon: "fh_docs_fill",
    tone: "crd",
  },
  dtr: {
    label: "DTR",
    icon: "fh_check_decagram",
    tone: "dtr",
  },
  pas: {
    label: "PAS",
    icon: "fh_secure_sheild",
    tone: "pas",
  },
};
